/* Copyright (c) 2020-present CloudIO Inc */

import { allSwichColumns } from 'componentregistry/components/DataGrid/columns/BaseColumn';
import { page as SamplePage } from '../../help/sample_pages/GridPage';
import { preventIfParentPathContainsAutoLayout } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  Item,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './DataGrid';

const name = 'Data Grid';

export type DataGridOrTable = 'DataGrid';

const DataGridManager: ComponentManagerFn<DataGridOrTable> = (
  platform: Platform,
  type: DataGridOrTable,
): ComponentManager<DataGridOrTable> => {
  return {
    ...baseComponentManager<DataGridOrTable>(platform, type),
    defaultValue,
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      const inAllowedList = (
        [
          ...allSwichColumns,
          'RowNumberColumn',
          'RowSelectionColumn',
          'SwitchColumn',
          'TriggerOnRowClick',
          'TriggerOnRowDoubleClick',
        ] as ItemType[]
      ).includes(childType);
      if (!inAllowedList) {
        return false;
      }
      if (childType === 'TreeColumn') {
        const children = platform.getChildrenOfType(
          appUid,
          pageId,
          page,
          item,
          childType,
        );
        return children.length === 0;
      }
      return true;
    },
    isAllowedInsideParent: (page, item) =>
      preventIfParentPathContainsAutoLayout(page, item, name),
    props: {
      enableFormulaColumn: {
        label: 'Enable Formula Column',
        type: 'boolean',
        category: 'Data',
      },
      filterable: {
        label: 'Filterable',
        type: 'boolean',
        category: 'Data',
      },
      sortable: {
        label: 'Sortable',
        type: 'boolean',
        category: 'Data',
      },
      showNoDataMsg: {
        label: 'Show No data message',
        type: 'boolean',
        category: 'Data',
        helpText: 'Show message when there are no records in the grid',
      },
      noDataMsg: {
        label: 'No data message',
        type: 'string',
        category: 'Data',
        helpText: 'Message to show when there are no records in the grid',
      },
      pinnable: {
        label: 'Pinnable',
        type: 'boolean',
      },
      insertable: {
        label: 'Insertable',
        type: 'booleanExpression',
        category: 'Data',
      },
      updatable: {
        label: 'Updatable',
        type: 'booleanExpression',
        category: 'Data',
      },
      showGridFilters: {
        label: 'Show Grid Filters',
        type: 'boolean',
      },
      allowDownArrowRecordCreation: {
        label: 'Allow Record Creation On Down Arrow',
        type: 'boolean',
        category: 'Data',
      },
      hideHeader: {
        label: 'Hide Header',
        type: 'boolean',
      },
      hideColumnMenu: {
        label: 'Hide Column Menu',
        type: 'boolean',
      },
      enableSpreadSheetCopy: {
        label: 'Spread Sheet Copy',
        type: 'boolean',
        helpText: 'Allow Drag And Paste',
      },
      allowHotKeyCopyPaste: {
        label: 'Allow Hot Key Copy Paste',
        type: 'boolean',
        helpText: 'Allow Drag And Paste',
      },
      allowInsertOnPaste: {
        label: 'Allow Record Creation On Hot Key Paste',
        type: 'boolean',
        helpText: 'Enable this to create New Record on  Paste',
      },
      backgroundColor: {
        label: 'BG Color',
        type: 'BGColor',
        category: 'Look & Feel',
        allowCustomColor: true,
      },
      showBorder: {
        label: 'Borders',
        type: 'boolean',
        category: 'Look & Feel',
      },
      showColumnLines: {
        label: 'Column Lines',
        type: 'boolean',
        category: 'Look & Feel',
      },
      showRowLines: {
        label: 'Row Lines',
        type: 'boolean',
        category: 'Look & Feel',
      },
      highlightRow: {
        label: 'Highlight Row',
        type: 'boolean',
        category: 'Look & Feel',
      },
      highlightActiveRow: {
        label: 'Highlight Active Row',
        type: 'boolean',
        category: 'Look & Feel',
      },
      stripeRows: {
        label: 'Stripe Rows',
        type: 'boolean',
        category: 'Look & Feel',
      },
      rowHeight: {
        label: 'Row Height',
        type: 'number',
        category: 'Look & Feel',
        min: 24,
        max: 100,
        step: 2,
      },
      disableReorder: {
        label: 'Disable Column Reordering',
        type: 'boolean',
        category: 'Look & Feel',
      },
      expandLastColumn: {
        label: 'Expand last column',
        type: 'boolean',
        category: 'Look & Feel',
      },
      autoFitColumns: {
        label: 'Auto Fit Columns',
        type: 'boolean',
        category: 'Look & Feel',
      },
      headerBackgroundColor: {
        label: 'Header BG Color',
        type: 'ColorExpression',
        category: 'Look & Feel',
        helpText: `Use one of the following values for a gray background with darkmode support.

divider
background.default
action.selected
primary.main
primary.dark
primary.light

Similar to how primary palette is used above, you can also use one of the following palette secondary, success, warning, error.
`,
      },
      rowBuffer: {
        label: 'Row Buffer',
        type: 'number',
        category: 'Performance',
        max: 10,
        min: 0,
        step: 0.1,
      },
      columnBuffer: {
        label: 'Column Buffer',
        type: 'number',
        category: 'Performance',
        max: 10,
        min: 0,
        step: 0.1,
      },
    },
    category: 'Data Containers',
    name,
    canHaveChildren: () => true,
    layout: 'fixed',
    excludeChildren: true,
    icon: { iconName: 'table' },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      switch (prop) {
        case 'headerBackgroundColor':
        case 'hideColumnMenu':
        case 'pinnable':
        case 'sortable':
        case 'filterable':
          return !itemData.hideHeader;
        case 'enableFormulaColumn':
          return !(itemData.hideHeader || itemData.hideColumnMenu);
        case 'allowInsertOnPaste':
          return itemData.allowHotKeyCopyPaste;
        default:
          return true;
      }
    },
    replaceableWith: ['CanvasGrid'],
    description:
      'Data grid is a tabular representation of data with rows and columns.',
    helpRenders: [
      {
        title: 'Basic Data Grid',
        description:
          'By default the Grid will have all the features enabled except for Editing.',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: [],
        item: {
          children: [
            'stringColumn',
            'dateColumn',
            'numberColumn',
            'checkboxColumn',
          ],
          itemId: 'dataGrid',
          parent: 'dataStoreReference',
          props: {},
          type: 'DataGrid',
        },
      },
      {
        title: 'Column Menu',
        description:
          'You can hide the column menu by turning on the Hide Column Menu prop.',
        page: SamplePage,
        exampleId: 'cm',
        clearParentChildren: true,
        visibleProps: ['hideColumnMenu'],
        item: {
          children: [
            'stringColumn',
            'dateColumn',
            'numberColumn',
            'checkboxColumn',
          ],
          itemId: 'dataGrid',
          parent: 'dataStoreReference',
          props: {
            hideColumnMenu: true,
          },
          type: 'DataGrid',
        },
      },
      {
        title: 'Formula Column, Filerable & Sortable Props',
        description:
          'You can turn off individual props to disable some of the features.',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        visibleProps: ['sortable', 'filterable', 'enableFormulaColumn'],
        item: {
          children: [
            'stringColumn',
            'dateColumn',
            'numberColumn',
            'checkboxColumn',
          ],
          itemId: 'dataGrid',
          parent: 'dataStoreReference',
          props: {
            sortable: false,
            filterable: false,
            enableFormulaColumn: false,
          },
          type: 'DataGrid',
        },
      },
      {
        title: 'Borders, Column & Row Lines',
        description:
          'You can turn off the Borders, Column and/or Rows Lines if needed.',
        page: SamplePage,
        exampleId: 'e3',
        clearParentChildren: true,
        visibleProps: ['showRowLines', 'showColumnLines', 'showBorder'],
        item: {
          children: [
            'stringColumn',
            'dateColumn',
            'numberColumn',
            'checkboxColumn',
          ],
          itemId: 'dataGrid',
          parent: 'dataStoreReference',
          props: {
            showRowLines: true,
            showColumnLines: false,
            showBorder: false,
            hideColumnMenu: true,
            sortable: false,
          },
          type: 'DataGrid',
        },
      },
      {
        title: 'Background Color',
        description:
          'You can change the background color between Default, Paper & Transparant.',
        page: SamplePage,
        exampleId: 'e4',
        clearParentChildren: true,
        visibleProps: ['backgroundColor'],
        item: {
          children: [
            'stringColumn',
            'dateColumn',
            'numberColumn',
            'checkboxColumn',
          ],
          itemId: 'dataGrid',
          parent: 'dataStoreReference',
          props: {
            backgroundColor: 'default',
            hideColumnMenu: true,
          },
          type: 'DataGrid',
        },
      },
      {
        title: 'Highlight Row & Strip Rows',
        description:
          "Turn on 'Strip Rows' prop to highlight alternative rows as shown below. 'Highlight Row' prop (which is on by default) will highlight rows when the user hovers the mouse.",
        page: SamplePage,
        exampleId: 'e5',
        clearParentChildren: true,
        visibleProps: ['stripeRows', 'highlightRow'],
        item: {
          children: [
            'stringColumn',
            'dateColumn',
            'numberColumn',
            'checkboxColumn',
          ],
          itemId: 'dataGrid',
          parent: 'dataStoreReference',
          props: {
            stripeRows: true,
            highlightRow: false,
            hideColumnMenu: true,
          },
          type: 'DataGrid',
        },
      },
      {
        title: 'Editable Data Grid',
        description:
          'You can keyboard to navigate between cells. Try using Page Down, Page Up, Home, End, Up/Down Arrow Keys & Tab.',
        page: SamplePage,
        exampleId: 'e6',
        clearParentChildren: true,
        visibleProps: ['updatable', 'expandLastColumn', 'autoFitColumns'],
        item: {
          children: [
            'stringColumn',
            'dateColumn',
            'numberColumn',
            'checkboxColumn',
          ],
          itemId: 'dataGrid',
          parent: 'dataStoreReference',
          props: {
            updatable: { type: 'boolean', value: true },
            hideColumnMenu: true,
            expandLastColumn: false,
            autoFitColumns: false,
          },
          type: 'DataGrid',
        },
      },
      {
        title: 'Pinnable',
        description:
          "You can pin columns either on the left or right side of the grid. Using the Column Menu, try changing the Pin Left & Pin Right options. By turning off the pinnable prop, the user can't change the existing pinned column settings.",
        page: {
          ...SamplePage,
          stringColumn: {
            ...SamplePage.stringColumn,
            props: { ...SamplePage.stringColumn.props, pinLeft: true },
          } as Item<'StringColumn'>,
          checkboxColumn: {
            ...SamplePage.checkboxColumn,
            props: { ...SamplePage.checkboxColumn.props, pinRight: true },
          } as Item<'CheckboxColumn'>,
          stringColumn1: {
            ...SamplePage.stringColumn,
            itemId: 'stringColumn1',
            props: {
              ...SamplePage.stringColumn.props,
              width: 300,
              label: { type: 'text', value: 'Name 1' },
            },
          } as Item<'StringColumn'>,
          stringColumn2: {
            ...SamplePage.stringColumn,
            itemId: 'stringColumn2',
            props: {
              ...SamplePage.stringColumn.props,
              width: 300,
              label: { type: 'text', value: 'Name 2' },
            },
          } as Item<'StringColumn'>,
          stringColumn3: {
            ...SamplePage.stringColumn,
            itemId: 'stringColumn3',
            props: {
              ...SamplePage.stringColumn.props,
              width: 300,
              label: { type: 'text', value: 'Name 3' },
            },
          } as Item<'StringColumn'>,
        },
        exampleId: 'e7',
        clearParentChildren: true,
        visibleProps: ['pinnable'],
        item: {
          children: [
            'stringColumn',
            'dateColumn',
            'numberColumn',
            'stringColumn1',
            'checkboxColumn',
            'stringColumn2',
            'stringColumn3',
          ],
          itemId: 'dataGrid',
          parent: 'dataStoreReference',
          props: {},
          type: 'DataGrid',
        },
      },
    ],
  };
};

export default DataGridManager;
